import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, canManageUsers } from '../../utils/auth';
import { getUsers, addUser, deleteUser, updateUserRole, createLogEntry, updateUser, updateUserStatus } from '../../utils/storage';
import { supabase } from '../../utils/supabase';
import type { User, UserRole } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function UserManagement() {
  const navigate = useNavigate();
  const userRef = useRef<User | null>(getUser());
  const user = userRef.current;
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [modalActionLoading, setModalActionLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleConfirmModal, setShowRoleConfirmModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);
  const [showLogoutTimerModal, setShowLogoutTimerModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [displayNameSuccess, setDisplayNameSuccess] = useState('');
  const [logoutTimerMinutes, setLogoutTimerMinutes] = useState(5);
  const [logoutTimerSuccess, setLogoutTimerSuccess] = useState('');
  const [newUser, setNewUser] = useState({ username: '', displayName: '', password: '', role: 'patient_registration' as UserRole });
  const [addUserError, setAddUserError] = useState('');

  useEffect(() => {
    if (!user || !canManageUsers(user)) {
      navigate('/dashboard');
      return;
    }
    loadUsers();

    // Real-time subscription: update is_online status instantly
    const channel = supabase
      .channel('app_users_online_status')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'app_users' },
        (payload) => {
          const updated = payload.new as Record<string, unknown>;
          setUsers((prev) =>
            prev.map((u) =>
              u.id === updated.id
                ? {
                    ...u,
                    isOnline: updated.is_online as boolean,
                    lastLoginAt: updated.last_login_at as string | undefined,
                    isActive: updated.is_active as boolean,
                  }
                : u
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoadingUsers(true);
    }
    const data = await getUsers();
    setUsers(data);
    setLoadingUsers(false);
    setRefreshing(false);
  };

  const handleAddUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) return;
    setModalActionLoading(true);
    setAddUserError('');

    const trimmedUsername = newUser.username.trim();

    // Pre-check for duplicate username before hitting the DB
    const existing = users.find(
      (u) => u.username.toLowerCase() === trimmedUsername.toLowerCase()
    );
    if (existing) {
      setAddUserError(`Username "${trimmedUsername}" already exists. Please choose a different username.`);
      setModalActionLoading(false);
      return;
    }

    const newUserData: User = {
      id: Date.now().toString(),
      username: trimmedUsername,
      displayName: newUser.displayName.trim() || undefined,
      password: newUser.password,
      role: newUser.role,
      createdAt: new Date().toISOString(),
      createdBy: user?.username || 'unknown',
      isActive: true,
      isOnline: false,
      logoutTimer: 5,
    };

    try {
      await addUser(newUserData);

      // Log the action using createLogEntry
      await createLogEntry(
        user,
        'User Management',
        `Added new user: ${newUserData.username} with role ${newUserData.role}`
      );

      setNewUser({ username: '', displayName: '', password: '', role: 'patient_registration' });
      setShowAddModal(false);
      setAddUserError('');
      loadUsers();
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === '23505') {
        setAddUserError(`Username "${trimmedUsername}" already exists. Please choose a different username.`);
      } else {
        setAddUserError('Failed to add user. Please try again.');
      }
    } finally {
      setModalActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setModalActionLoading(true);
    await deleteUser(selectedUser.id);

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `Deleted user: ${selectedUser.username}`
    );

    setShowDeleteModal(false);
    setSelectedUser(null);
    setModalActionLoading(false);
    loadUsers();
  };

  const initiateRoleChange = (targetUser: User, newRole: UserRole) => {
    setSelectedUser(targetUser);
    setPendingRole(newRole);
    setShowRoleConfirmModal(true);
  };

  const confirmRoleChange = async () => {
    if (!selectedUser || !pendingRole) return;
    setModalActionLoading(true);
    await updateUserRole(selectedUser.id, pendingRole);

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `Changed role of ${selectedUser.username} from ${selectedUser.role} to ${pendingRole}`
    );

    setShowRoleConfirmModal(false);
    setSelectedUser(null);
    setPendingRole(null);
    setModalActionLoading(false);
    loadUsers();
  };

  const openPasswordModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (!newPassword.trim()) { setPasswordError('Password cannot be empty.'); return; }
    if (newPassword.length < 4) { setPasswordError('Password must be at least 4 characters.'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match.'); return; }

    setModalActionLoading(true);
    if (selectedUser) {
      await updateUser(selectedUser.id, { password: newPassword });
    }

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `Updated password for user: ${selectedUser?.username}`
    );

    setPasswordSuccess('Password updated successfully!');
    setModalActionLoading(false);
    setTimeout(() => {
      setShowPasswordModal(false);
      setSelectedUser(null);
    }, 1200);
    loadUsers();
  };

  const openDisplayNameModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    setEditDisplayName(targetUser.displayName || '');
    setDisplayNameSuccess('');
    setShowDisplayNameModal(true);
  };

  const handleUpdateDisplayName = async () => {
    if (!selectedUser) return;
    setModalActionLoading(true);
    await updateUser(selectedUser.id, { displayName: editDisplayName.trim() || undefined });

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `Updated display name for user: ${selectedUser.username} → "${editDisplayName.trim()}"`
    );

    setDisplayNameSuccess('Display name updated!');
    setModalActionLoading(false);
    setTimeout(() => {
      setShowDisplayNameModal(false);
      setSelectedUser(null);
    }, 1000);
    loadUsers();
  };

  const openLogoutTimerModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    setLogoutTimerMinutes(targetUser.logoutTimer || 5);
    setLogoutTimerSuccess('');
    setShowLogoutTimerModal(true);
  };

  const handleUpdateLogoutTimer = async () => {
    if (!selectedUser) return;
    if (logoutTimerMinutes < 1 || logoutTimerMinutes > 120) return;
    setModalActionLoading(true);

    await updateUser(selectedUser.id, { logoutTimer: logoutTimerMinutes });

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `Updated logout timer for user: ${selectedUser.username} → ${logoutTimerMinutes} minutes`
    );

    setLogoutTimerSuccess('Logout timer updated!');
    setModalActionLoading(false);
    setTimeout(() => {
      setShowLogoutTimerModal(false);
      setSelectedUser(null);
    }, 1000);
    loadUsers();
  };

  const initiateStatusChange = (targetUser: User, activate: boolean) => {
    setSelectedUser(targetUser);
    setPendingStatus(activate);
    setShowStatusModal(true);
  };

  const confirmStatusChange = async () => {
    if (!selectedUser) return;
    setModalActionLoading(true);
    await updateUserStatus(selectedUser.id, pendingStatus ? 'active' : 'inactive');

    // Log the action using createLogEntry
    await createLogEntry(
      user,
      'User Management',
      `${pendingStatus ? 'Reactivated' : 'Deactivated'} user: ${selectedUser.username}`
    );

    setShowStatusModal(false);
    setSelectedUser(null);
    setModalActionLoading(false);
    loadUsers();
  };

  const getRoleDisplay = (role: string) =>
    role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const formatLastLogin = (lastLoginAt?: string) => {
    if (!lastLoginAt) return 'Never';
    return new Date(lastLoginAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const roles: UserRole[] = [
    'admin', 'patient_registration', 'cashier', 'bp', 'heart_rate', 'spo2',
    'temperature', 'rbs', 'cholesterol', 'height_weight', 'blood_grouping',
    'colour_blindness', 'visual_acuity', 'patient_monitor', 'view_only', 'all_vitals', 'body_fat',
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
              <p className="text-sm text-gray-500 mt-1">Manage system users, roles and access</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadUsers(true)}
                disabled={refreshing}
                title="Refresh online status"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                <i className={`ri-refresh-line ${refreshing ? 'animate-spin' : ''}`}></i>
                {refreshing ? 'Refreshing...' : 'Refresh Status'}
              </button>
              <button
                onClick={() => { setShowAddModal(true); setAddUserError(''); }}
                className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line"></i>
                Add User
              </button>
            </div>
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/60">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Username</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Display Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Role</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Last Login</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Created By</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isActive = u.status !== 'inactive' && u.isActive !== false;
                    const isRowLoading = actionLoadingId === u.id;
                    return (
                      <tr key={u.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${!isActive ? 'opacity-60' : ''}`}>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isActive ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                              {(u.displayName || u.username).charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium text-gray-800">{u.username}</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          {u.displayName ? (
                            <span className="text-sm text-gray-800 font-medium">{u.displayName}</span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Not set</span>
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          {u.role === 'master_admin' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-semibold">
                              <i className="ri-shield-star-line"></i>
                              Master Admin
                            </span>
                          ) : (
                            <select
                              value={u.role}
                              onChange={(e) => initiateRoleChange(u, e.target.value as UserRole)}
                              className="text-xs px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none cursor-pointer bg-white"
                            >
                              {roles.map((role) => (
                                <option key={role} value={role}>{getRoleDisplay(role)}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${u.isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                              <span className={`text-xs font-medium ${u.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                                {u.isOnline ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            {!isActive && (
                              <span className="text-[10px] text-red-500 font-medium">Deactivated</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="text-xs text-gray-500">{formatLastLogin(u.lastLoginAt)}</span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="text-xs text-gray-500 font-mono">{u.createdBy || '—'}</span>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {isRowLoading ? (
                              <div className="flex items-center gap-1.5 px-3 py-1.5">
                                <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-xs text-gray-500">Processing...</span>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => { setActionLoadingId(u.id); openDisplayNameModal(u); setActionLoadingId(null); }}
                                  className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                  title="Set Display Name"
                                >
                                  <i className="ri-id-card-line"></i>
                                  Display Name
                                </button>
                                <button
                                  onClick={() => { setActionLoadingId(u.id); openLogoutTimerModal(u); setActionLoadingId(null); }}
                                  className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                  title="Set Logout Timer"
                                >
                                  <i className="ri-timer-line"></i>
                                  Logout Timer
                                </button>
                                <button
                                  onClick={() => { setActionLoadingId(u.id); openPasswordModal(u); setActionLoadingId(null); }}
                                  className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-orange-50 text-orange-700 hover:bg-orange-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                  title="Update Password"
                                >
                                  <i className="ri-lock-password-line"></i>
                                  Password
                                </button>
                                {u.role !== 'master_admin' && (
                                  <>
                                    {isActive ? (
                                      <button
                                        onClick={() => initiateStatusChange(u, false)}
                                        className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                      >
                                        <i className="ri-user-forbid-line"></i>
                                        Deactivate
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => initiateStatusChange(u, true)}
                                        className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                      >
                                        <i className="ri-user-follow-line"></i>
                                        Reactivate
                                      </button>
                                    )}
                                    <button
                                      onClick={() => { setSelectedUser(u); setShowDeleteModal(true); }}
                                      className="flex items-center gap-1 text-[11px] px-2 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                      Delete
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Add New User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="Enter username (used for login)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                  <span className="ml-1 text-xs text-gray-400 font-normal">(shown on dashboard &amp; sidebar)</span>
                </label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="e.g. Dr. Arjun Kumar (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="Enter password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm cursor-pointer"
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>{getRoleDisplay(role)}</option>
                  ))}
                </select>
              </div>
              <div className="bg-gray-50 rounded-lg px-4 py-2.5 text-xs text-gray-500 flex items-center gap-2">
                <i className="ri-user-line text-gray-400"></i>
                Will be created by: <strong className="text-gray-700">{user?.username}</strong>
              </div>
              {addUserError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {addUserError}
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddUser}
                disabled={!newUser.username.trim() || !newUser.password.trim() || modalActionLoading}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Adding...</>
                ) : 'Add User'}
              </button>
              <button
                onClick={() => { setShowAddModal(false); setAddUserError(''); setNewUser({ username: '', displayName: '', password: '', role: 'patient_registration' }); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Display Name Modal */}
      {showDisplayNameModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-id-card-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1 text-center">Set Display Name</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              For <strong>{selectedUser.username}</strong> — shown on dashboard, sidebar &amp; reports
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
              <input
                type="text"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                placeholder="e.g. Dr. Arjun Kumar"
              />
              <p className="text-xs text-gray-400 mt-1">Leave blank to use username as display name.</p>
            </div>
            {displayNameSuccess && (
              <div className="mt-3 bg-green-50 border border-green-200 text-green-600 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>
                {displayNameSuccess}
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateDisplayName}
                disabled={modalActionLoading}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>
                ) : 'Save'}
              </button>
              <button
                onClick={() => { setShowDisplayNameModal(false); setSelectedUser(null); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Timer Modal */}
      {showLogoutTimerModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-timer-line text-2xl text-amber-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1 text-center">Set Logout Timer</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              For <strong>{selectedUser.displayName || selectedUser.username}</strong> — auto logout after inactivity
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timeout Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="120"
                value={logoutTimerMinutes}
                onChange={(e) => setLogoutTimerMinutes(parseInt(e.target.value) || 5)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none text-sm"
                placeholder="Enter minutes (1-120)"
              />
              <p className="text-xs text-gray-400 mt-1">User will be logged out after this many minutes of inactivity. Default is 5 minutes.</p>
            </div>
            {logoutTimerSuccess && (
              <div className="mt-3 bg-green-50 border border-green-200 text-green-600 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>
                {logoutTimerSuccess}
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateLogoutTimer}
                disabled={logoutTimerMinutes < 1 || logoutTimerMinutes > 120 || modalActionLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>
                ) : 'Save'}
              </button>
              <button
                onClick={() => { setShowLogoutTimerModal(false); setSelectedUser(null); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Confirmation Modal */}
      {showRoleConfirmModal && selectedUser && pendingRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-user-line text-2xl text-amber-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">Confirm Role Change</h3>
            <p className="text-sm text-gray-600 mb-1 text-center">
              You are about to change the role of <strong>{selectedUser.displayName || selectedUser.username}</strong>:
            </p>
            <div className="flex items-center justify-center gap-3 my-4">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                {getRoleDisplay(selectedUser.role)}
              </span>
              <i className="ri-arrow-right-line text-gray-400"></i>
              <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                {getRoleDisplay(pendingRole)}
              </span>
            </div>
            <p className="text-xs text-gray-500 text-center mb-6">This will immediately affect the user&apos;s access permissions.</p>
            <div className="flex gap-3">
              <button
                onClick={confirmRoleChange}
                disabled={modalActionLoading}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Updating...</>
                ) : 'Confirm Change'}
              </button>
              <button
                onClick={() => { setShowRoleConfirmModal(false); setSelectedUser(null); setPendingRole(null); loadUsers(); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-password-line text-2xl text-orange-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-1 text-center">Update Password</h3>
            <p className="text-sm text-gray-500 text-center mb-5">
              Set a new password for <strong>{selectedUser.displayName || selectedUser.username}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Enter new password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Confirm new password" />
              </div>
              {passwordError && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"><i className="ri-error-warning-line"></i>{passwordError}</div>}
              {passwordSuccess && <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"><i className="ri-checkbox-circle-line"></i>{passwordSuccess}</div>}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdatePassword}
                disabled={modalActionLoading}
                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Updating...</>
                ) : 'Update Password'}
              </button>
              <button
                onClick={() => { setShowPasswordModal(false); setSelectedUser(null); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate / Reactivate Modal */}
      {showStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${pendingStatus ? 'bg-green-50' : 'bg-orange-50'}`}>
              <i className={`text-2xl ${pendingStatus ? 'ri-user-follow-line text-green-600' : 'ri-user-forbid-line text-orange-600'}`}></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">{pendingStatus ? 'Reactivate User' : 'Deactivate User'}</h3>
            <p className="text-sm text-gray-600 text-center mb-2">
              Are you sure you want to <strong>{pendingStatus ? 'reactivate' : 'deactivate'}</strong> <strong>{selectedUser.displayName || selectedUser.username}</strong>?
            </p>
            {!pendingStatus && (
              <p className="text-xs text-orange-600 bg-orange-50 border border-orange-100 rounded-lg px-4 py-2.5 text-center mb-4">
                <i className="ri-information-line mr-1"></i>
                This user will no longer be able to log in until reactivated.
              </p>
            )}
            <div className="flex gap-3 mt-4">
              <button
                onClick={confirmStatusChange}
                disabled={modalActionLoading}
                className={`flex-1 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 ${pendingStatus ? 'bg-green-500 hover:bg-green-600' : 'bg-orange-500 hover:bg-orange-600'}`}
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Processing...</>
                ) : (pendingStatus ? 'Yes, Reactivate' : 'Yes, Deactivate')}
              </button>
              <button
                onClick={() => { setShowStatusModal(false); setSelectedUser(null); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">Confirm Delete</h3>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Are you sure you want to delete <strong>{selectedUser.displayName || selectedUser.username}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteUser}
                disabled={modalActionLoading}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                {modalActionLoading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Deleting...</>
                ) : 'Delete'}
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
                disabled={modalActionLoading}
                className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
